import { captureException } from '@sentry/react';
import { publish } from '@bynder/micro-frontend-communication';
import type { MetapropertiesOption, SearchMetaproperty } from '../types';
import {
	isTextSearch,
	type SearchParams,
	type SimilarityFilter,
	type TextSearchParams,
} from './types';

/** Context for AI: the asset-bank/search half of the custom events migration — maps Spotlight's flat SearchParams onto the topic's filter union. */
type SearchMessage = Parameters<typeof publish<'asset-bank/search'>>[1];
type SearchFilter = SearchMessage['filters'][number];

const publishFilter = (isHybridSearchOn: boolean, filter: SearchFilter) => {
	publish('asset-bank/search', { filters: [filter], isHybridSearchOn });
};

/**
 * The types only let a text search reach `publishTextSearch`, so this is the last-resort
 * net for an untyped caller. Report to Sentry rather than drop: the topic is the only
 * transport to paramount, so a silent skip would lose the search.
 */
const reportUnmappedSearch = (params: SearchParams) => {
	captureException(
		new Error(`Spotlight: search field "${params.field}" has no asset-bank/search equivalent`),
		{ extra: { params } },
	);
};

export const publishTextSearch = (isHybridSearchOn: boolean, params: TextSearchParams) => {
	if (!isTextSearch(params)) {
		reportUnmappedSearch(params);

		return;
	}

	publishFilter(isHybridSearchOn, {
		type: params.field === 'singletext' ? 'sticky-text' : 'non-sticky-text',
		action: params.filterType,
		value: params.value,
		...(params.searchOptions ? { searchOptions: params.searchOptions } : {}),
	});
};

export const publishTagSearch = (isHybridSearchOn: boolean, tagName: string) => {
	publishFilter(isHybridSearchOn, { type: 'tag', action: 'add', value: tagName });
};

/**
 * The raw-DOM payload flattens a metaproperty option into `field:
 * metaproperty_<name>` plus a value, losing both ids and the option's real
 * label. The topic carries them, so paramount can stop re-deriving them.
 */
export const publishMetapropertyOptionSearch = (
	isHybridSearchOn: boolean,
	metaproperty: SearchMetaproperty,
	option: MetapropertiesOption,
) => {
	publishFilter(isHybridSearchOn, {
		type: 'metapropertyOption',
		action: 'add',
		metaproperty: { id: metaproperty.ID, name: metaproperty.name, label: metaproperty.label },
		option: {
			id: option.id,
			name: option.name,
			label: option.label,
			...(option.image ? { image: option.image } : {}),
			...(option.icon ? { icon: option.icon } : {}),
		},
	});
};

export const publishSimilaritySearch = (isHybridSearchOn: boolean, filter: SimilarityFilter) => {
	if (filter.similarToImageData) {
		publishFilter(isHybridSearchOn, {
			type: 'similarity',
			action: 'add',
			similarTo: { field: 'imageData', value: filter.similarToImageData },
		});

		return;
	}

	if (filter.similarToURL) {
		publishFilter(isHybridSearchOn, {
			type: 'similarity',
			action: 'add',
			similarTo: { field: 'imageUrl', value: filter.similarToURL },
		});

		return;
	}

	if (filter.similarToText) {
		publishFilter(isHybridSearchOn, {
			type: 'similarity',
			action: 'add',
			similarTo: { field: 'text', value: filter.similarToText },
		});

		return;
	}

	// There is no way in the Spotlight UI to set similarToAssetIds, so
	// it is deliberately unmapped. Reported rather than left to fall through
	// because otherwise we would publish a similarity filter reset.
	if (filter.similarToAssetIds) {
		captureException(
			new Error('Spotlight: similarToAssetIds has no asset-bank/search mapping'),
			{ extra: { filter } },
		);

		return;
	}

	// When the NLS toggle is switched off, is sends all four null, meaning "Reset"
	publishFilter(isHybridSearchOn, { type: 'similarity', action: 'remove' });
};
